(() => {
  const DELIVERY_MODE = 'delivery';
  const STORAGE_MODE = 'fulfillment-mode';
  const STORAGE_STORE = 'selected-store';
  const CACHE_KEY = 'ld-location-inventory-v3';
  const STORE_ALIASES = {
    'apple-valley': ['lets-dish-av', 'let-s-dish-av', 'av'],
    'lets-dish-av': ['apple-valley', 'let-s-dish-av', 'av'],
    'eden-prairie': ['lets-dish-ep', 'let-s-dish-ep', 'ep'],
    'lets-dish-ep': ['eden-prairie', 'let-s-dish-ep', 'ep'],
    'maple-grove': ['lets-dish-mg', 'let-s-dish-mg', 'mg'],
    'lets-dish-mg': ['maple-grove', 'let-s-dish-mg', 'mg'],
    'prior-lake': ['lets-dish-prior-lake', 'let-s-dish-prior-lake'],
    'lets-dish-prior-lake': ['prior-lake', 'let-s-dish-prior-lake'],
    'woodbury': ['lets-dish-wb', 'let-s-dish-wb', 'wb'],
    'lets-dish-wb': ['woodbury', 'let-s-dish-wb', 'wb']
  };
  const STORE_ABBREV = {
    'apple-valley': 'av',
    'eden-prairie': 'ep',
    'maple-grove': 'mg',
    'woodbury': 'wb'
  };
  const HIDDEN_ATTR = 'data-fulfillment-hidden';
  const CARD_SELECTOR = '[data-js-product-item][data-variant-id], [data-js-search-item][data-variant-id]';
  const PAGE_SELECTOR = '[data-js-product-component][data-variant-id]';

  const memoryCache = new Map();
  const inflight = new Map();
  let filterToken = 0;

  function getDeliveryCollectionPath() {
    const el = document.querySelector('store-selector[data-main-selector]');
    return ((el && el.dataset.deliveryCollection) || '/collections/delivery').replace(/\/$/, '');
  }

  function isDeliveryCollectionPath() {
    const path = window.location.pathname.replace(/\/$/, '');
    const deliveryPath = getDeliveryCollectionPath();
    return path === deliveryPath || path.endsWith(deliveryPath) || path.includes(`${deliveryPath}/`);
  }

  function getMode() {
    if (isDeliveryCollectionPath()) return DELIVERY_MODE;
    return localStorage.getItem(STORAGE_MODE) || 'pickup';
  }

  function getStore() {
    return localStorage.getItem(STORAGE_STORE);
  }

  function getDeliveryInventoryStore() {
    const el = document.querySelector('store-selector[data-main-selector]');
    return (el && el.dataset.deliveryInventoryStore) || 'lets-dish-av';
  }

  function getInventoryStore() {
    if (getMode() === DELIVERY_MODE) {
      return getDeliveryInventoryStore() || 'apple-valley';
    }
    return getStore();
  }

  function getConfiguredInventoryMap() {
    const selector = document.querySelector('store-selector[data-main-selector]');
    if (selector && selector.inventoryMap && Object.keys(selector.inventoryMap).length) {
      return selector.inventoryMap;
    }
    const script = selector && selector.querySelector('[data-js-inventory-map]');
    if (!script) return {};
    try {
      return JSON.parse(script.textContent) || {};
    } catch (error) {
      return {};
    }
  }

  function resolveInventoryHandles(storeHandle) {
    const handles = new Set();
    if (!storeHandle) return [];
    handles.add(storeHandle);

    const configured = getConfiguredInventoryMap()[storeHandle];
    if (configured) handles.add(configured);

    const selector = document.querySelector('store-selector[data-main-selector]');
    const item = selector && selector.querySelector(`.store-selector-item[data-handle="${storeHandle}"]`);
    if (item && item.dataset.inventoryHandle) {
      handles.add(item.dataset.inventoryHandle);
    }

    (STORE_ALIASES[storeHandle] || []).forEach((alias) => handles.add(alias));
    return Array.from(handles);
  }

  function getStoreLabel(handle) {
    const selector = document.querySelector('store-selector[data-main-selector]');
    if (selector && selector.storesList && selector.storesList[handle]) {
      return selector.storesList[handle];
    }
    return (handle || '').replace(/-/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  }

  function locale(key, fallback) {
    return (window.KROWN && window.KROWN.settings && window.KROWN.settings.locales && window.KROWN.settings.locales[key]) || fallback;
  }

  function readSessionCache() {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (error) {
      return {};
    }
  }

  function writeSessionCache(cache) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      /* ignore quota */
    }
  }

  function cacheGet(variantId) {
    if (memoryCache.has(variantId)) return memoryCache.get(variantId);
    const session = readSessionCache();
    if (session[variantId]) {
      memoryCache.set(variantId, session[variantId]);
      return session[variantId];
    }
    return null;
  }

  function cacheSet(variantId, map) {
    memoryCache.set(variantId, map);
    const session = readSessionCache();
    session[variantId] = map;
    writeSessionCache(session);
  }

  function mapFromElement(el) {
    const raw = el.getAttribute('data-store-availability');
    if (!raw || raw === '{}') return null;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && Object.keys(parsed).length) return parsed;
    } catch (error) {
      return null;
    }
    return null;
  }

  function parseAvailabilityDoc(root) {
    const map = {};
    if (!root) return map;
    root.querySelectorAll('.pickup-availability-alert[data-store]').forEach((el) => {
      map[el.dataset.store] = !!el.querySelector('.alert--success');
    });
    const ships = root.querySelector('[data-ld-ships]');
    if (ships) map._ships = ships.dataset.ldShips === 'true';
    return map;
  }

  async function fetchAvailabilityMap(variantId) {
    const cached = cacheGet(variantId);
    if (cached) return cached;
    if (inflight.has(variantId)) return inflight.get(variantId);

    const request = (async () => {
      const base = window.Shopify?.routes?.root || '/';
      const url = `${base}variants/${variantId}/?section_id=helper-pickup-availability-compact`;
      const response = await fetch(url);
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const map = parseAvailabilityDoc(doc);
      cacheSet(variantId, map);
      return map;
    })().catch(() => {
      inflight.delete(variantId);
      return null;
    });

    inflight.set(variantId, request);
    const result = await request;
    inflight.delete(variantId);
    return result;
  }

  // Liquid escapes the apostrophe in location names before handleize, so
  // "Let's Dish - AV" arrives as "let-39-s-dish-av". Normalize to "lets-dish-av".
  function normalizeMap(map) {
    const out = {};
    Object.keys(map).forEach((key) => {
      const clean = key.replace(/-39-/g, '');
      out[clean] = out[clean] === true || map[key] === true;
    });
    return out;
  }

  function isStoreInMap(rawMap, storeHandle) {
    if (!rawMap || !storeHandle) return false;
    const map = normalizeMap(rawMap);
    // Shipping: only Delivery-tagged products are stocked at AV Shipping. Maps without
    // the flag (single-store probes from other components) are not gated here.
    const ships = map._ships;
    delete map._ships;
    if (getMode() === DELIVERY_MODE && ships === false) return false;
    const handles = resolveInventoryHandles(storeHandle);
    // Shipping locations are not pickup-enabled, so they never appear in
    // store_availabilities. Fall back to the store they mirror (AV Shipping = AV).
    handles.slice().forEach((handle) => {
      if (/-shipping$/.test(handle)) handles.push(handle.replace(/-shipping$/, ''));
    });
    for (const handle of handles) {
      if (map[handle] === true) return true;
    }
    for (const handle of handles) {
      const match = Object.keys(map).find((key) => {
        return key === handle || key.endsWith(`-${handle}`) || key.includes(handle) || handle.includes(key);
      });
      if (match && map[match] === true) return true;
    }
    const abbrev = STORE_ABBREV[storeHandle];
    if (abbrev) {
      const match = Object.keys(map).find((key) => key === abbrev || key.endsWith(`-${abbrev}`));
      if (match) return map[match] === true;
    }
    return false;
  }

  // Gift cards are digital: no store stock, no pickup or shipping. Never gate them by store.
  // Covers native gift cards and the Giftnote product (type "Gift Cards", not a native gift card).
  function isLocationExempt(el) {
    if (!el) return false;
    if (el.hasAttribute && el.hasAttribute('data-location-exempt')) return true;
    return !!(el.closest && el.closest('[data-location-exempt]'));
  }

  async function isAvailableAtStore(variantId, storeHandle, el) {
    if (isLocationExempt(el)) return true;
    if (!variantId || !storeHandle) return true;

    const embedded = el ? mapFromElement(el) : null;
    if (embedded) {
      cacheSet(variantId, embedded);
      return isStoreInMap(embedded, storeHandle);
    }

    const cached = cacheGet(variantId);
    if (cached) return isStoreInMap(cached, storeHandle);

    const map = await fetchAvailabilityMap(variantId);
    if (!map) return true;
    return isStoreInMap(map, storeHandle);
  }

  function hideEl(el) {
    el.style.display = 'none';
    el.setAttribute(HIDDEN_ATTR, 'true');
    el.setAttribute('aria-hidden', 'true');
  }

  function showEl(el) {
    el.style.display = '';
    el.removeAttribute(HIDDEN_ATTR);
    el.removeAttribute('aria-hidden');
  }

  function revealAll() {
    document.querySelectorAll(`[${HIDDEN_ATTR}="true"]`).forEach(showEl);
    document.querySelectorAll('[data-js-location-empty]').forEach((el) => {
      el.hidden = true;
      el.textContent = '';
    });
    document.querySelectorAll('[data-js-location-section-hidden]').forEach((section) => {
      section.style.display = '';
      section.removeAttribute('data-js-location-section-hidden');
    });
    document.querySelectorAll('[data-js-location-purchase-locked]').forEach((el) => {
      unlockPurchase(el);
    });
  }

  function unavailableMessage(root) {
    let message = root.querySelector('[data-js-location-unavailable]');
    if (message) return message;
    message = document.createElement('p');
    message.className = 'product-location-unavailable';
    message.setAttribute('data-js-location-unavailable', '');
    message.textContent = locale('products_unavailable_variant', 'Unavailable');
    const button = root.querySelector('[data-js-product-add-to-cart], [name="add"]');
    if (button && button.parentNode) {
      button.parentNode.insertBefore(message, button);
    } else {
      root.appendChild(message);
    }
    return message;
  }

  function stickyAddToCart() {
    return document.querySelector('sticky-add-to-cart');
  }

  function lockPurchase(root) {
    root.setAttribute('data-js-location-purchase-locked', 'true');
    const message = unavailableMessage(root);
    message.hidden = false;
    message.textContent = locale('products_unavailable_variant', 'Unavailable');
    root.querySelectorAll('[data-js-product-add-to-cart], [name="add"]').forEach((button) => {
      button.classList.add('disabled');
      button.setAttribute('disabled', 'disabled');
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('hidden', 'hidden');
    });
    const sticky = stickyAddToCart();
    if (sticky) sticky.setAttribute(HIDDEN_ATTR, 'true');
  }

  function unlockPurchase(root) {
    root.removeAttribute('data-js-location-purchase-locked');
    const message = root.querySelector('[data-js-location-unavailable]');
    if (message) message.hidden = true;
    root.querySelectorAll('[data-js-product-add-to-cart], [name="add"]').forEach((button) => {
      button.removeAttribute('hidden');
      if (root.getAttribute('data-availability') === 'false') return;
      button.classList.remove('disabled');
      button.removeAttribute('disabled');
      button.removeAttribute('aria-disabled');
    });
    const sticky = stickyAddToCart();
    if (sticky) sticky.removeAttribute(HIDDEN_ATTR);
  }

  function isMainProductPage(el) {
    return document.body.classList.contains('template-product') && el.classList.contains('main-product');
  }

  function isQuickView(el) {
    return !!el.closest('#product-quick-view, .product-quick-view__product, modal-box, [data-js-quick-view]');
  }

  function variantIdFrom(el) {
    const input = el.querySelector?.('form[action*="/cart/add"] [name="id"], product-form [name="id"], [name="id"]');
    return input?.value
      || el.querySelector?.('pickup-availability-compact')?.dataset.variantId
      || el.getAttribute('data-variant-id')
      || '';
  }

  // The card's variant is the first size available anywhere. When it is out at this
  // store, check the product's other sizes before hiding the card.
  async function anyOtherVariantAvailable(card, variantId, storeHandle) {
    const embedded = mapFromElement(card);
    if (getMode() === DELIVERY_MODE && embedded && embedded._ships === false) return false;
    const others = (card.getAttribute('data-variant-ids') || '').split(',').filter((id) => id && id !== String(variantId));
    for (const id of others) {
      if (await isAvailableAtStore(id, storeHandle, null)) return true;
    }
    return false;
  }

  async function applyCard(card, storeHandle) {
    if (isLocationExempt(card)) {
      showEl(card);
      return true;
    }
    const variantId = variantIdFrom(card);
    let available = await isAvailableAtStore(variantId, storeHandle, card);
    if (!available) available = await anyOtherVariantAvailable(card, variantId, storeHandle);
    if (available) {
      showEl(card);
    } else {
      hideEl(card);
    }
    return available;
  }

  // Product page / quick view opened on a size that is out at the chosen store (theme
  // defaults to the first variant available anywhere): switch once to a size that is in
  // stock there. Skipped when the URL names a variant, or for multi-option products.
  async function selectAvailableVariant(page, storeHandle) {
    if (page.getAttribute('data-ld-variant-autoselect') === storeHandle) return false;
    page.setAttribute('data-ld-variant-autoselect', storeHandle);
    if (isMainProductPage(page) && new URLSearchParams(window.location.search).has('variant')) return false;
    const inputs = Array.from(page.querySelectorAll('input.product-variant__input[data-variant-id]'));
    if (inputs.length < 2 || new Set(inputs.map((input) => input.name)).size !== 1) return false;
    for (const input of inputs) {
      if (input.checked || input.classList.contains('disabled')) continue;
      const map = cacheGet(input.dataset.variantId) || await fetchAvailabilityMap(input.dataset.variantId);
      if (map && isStoreInMap(map, storeHandle)) {
        input.click();
        return true;
      }
    }
    return false;
  }

  async function applyProductPage(page, storeHandle) {
    if (isLocationExempt(page)) {
      showEl(page);
      const exemptSection = page.closest('.shopify-section');
      if (exemptSection && exemptSection.hasAttribute('data-js-location-section-hidden')) {
        exemptSection.style.display = '';
        exemptSection.removeAttribute('data-js-location-section-hidden');
      }
      unlockPurchase(page);
      return true;
    }
    const variantId = variantIdFrom(page);
    if (variantId) page.setAttribute('data-variant-id', variantId);
    const available = await isAvailableAtStore(variantId, storeHandle, page);

    if (isMainProductPage(page) || isQuickView(page)) {
      showEl(page);
      if (!available && await selectAvailableVariant(page, storeHandle)) {
        // The variant change re-runs the filter for the new size.
        return true;
      }
      if (available) {
        unlockPurchase(page);
      } else {
        lockPurchase(page);
      }
      return available;
    }

    const section = page.closest('.shopify-section');
    if (available) {
      showEl(page);
      if (section) {
        section.style.display = '';
        section.removeAttribute('data-js-location-section-hidden');
      }
      unlockPurchase(page);
    } else if (section) {
      section.style.display = 'none';
      section.setAttribute('data-js-location-section-hidden', 'true');
    } else {
      hideEl(page);
    }
    return available;
  }

  function updateCartRecommendations() {
    /* Cart drawer "Often bought together": hide the heading and slider when every suggestion is filtered out. */
    document.querySelectorAll('cart-recommendations').forEach((holder) => {
      const items = holder.querySelectorAll('[data-js-product-item][data-variant-id]');
      if (!items.length) return;
      const visible = Array.from(items).filter((item) => item.getAttribute(HIDDEN_ATTR) !== 'true');
      if (!visible.length) {
        holder.style.display = 'none';
        holder.setAttribute('data-js-location-section-hidden', 'true');
      } else if (holder.hasAttribute('data-js-location-section-hidden')) {
        holder.style.display = '';
        holder.removeAttribute('data-js-location-section-hidden');
      }
    });
  }

  function updateEmptyStates(storeHandle) {
    updateCartRecommendations();

    const mode = getMode();
    const storeLabel = getStoreLabel(storeHandle);
    const emptyText = mode === DELIVERY_MODE
      ? locale('location_inventory_empty_delivery', 'Nothing is available for shipping right now. Check back soon.')
      : locale('location_inventory_empty', 'Nothing is in stock at %store% right now. Try another store or check back soon.').replace('%store%', storeLabel);
    const showingText = mode === DELIVERY_MODE
      ? locale('location_inventory_showing_delivery', 'Showing items available for shipping')
      : locale('location_inventory_showing', 'Showing items in stock at %store%').replace('%store%', storeLabel);

    document.querySelectorAll('#main-collection-product-grid, #CollectionProductGrid .collection, .collection.grid').forEach((grid) => {
      const items = grid.querySelectorAll('[data-js-product-item][data-variant-id]');
      if (!items.length) return;

      const visible = Array.from(items).filter((item) => item.getAttribute(HIDDEN_ATTR) !== 'true');
      let emptyEl = grid.querySelector('[data-js-location-empty]');
      if (!emptyEl) {
        emptyEl = document.createElement('p');
        emptyEl.className = 'no-content-message';
        emptyEl.setAttribute('data-js-location-empty', '');
        grid.appendChild(emptyEl);
      }
      if (!visible.length) {
        emptyEl.textContent = emptyText;
        emptyEl.hidden = false;
      } else {
        emptyEl.hidden = true;
        emptyEl.textContent = '';
      }
    });

    document.querySelectorAll('.search-block').forEach((block) => {
      const items = block.querySelectorAll('[data-js-search-item][data-variant-id]');
      if (!items.length) return;
      const visible = Array.from(items).filter((item) => item.getAttribute(HIDDEN_ATTR) !== 'true');
      const title = block.querySelector('.search-title');
      block.style.display = visible.length ? '' : 'none';
      if (title) title.style.display = visible.length ? '' : 'none';
    });

    document.querySelectorAll('[data-js-search-results]').forEach((holder) => {
      const productItems = holder.querySelectorAll('[data-js-search-item][data-variant-id]');
      if (!productItems.length) return;
      const visibleProducts = Array.from(productItems).filter((item) => item.getAttribute(HIDDEN_ATTR) !== 'true');
      let emptyEl = holder.querySelector('[data-js-search-location-empty]');
      if (!visibleProducts.length) {
        if (!emptyEl) {
          emptyEl = document.createElement('p');
          emptyEl.className = 'search-item';
          emptyEl.setAttribute('data-js-search-location-empty', '');
          const productsBlock = holder.querySelector('.search-block');
          (productsBlock || holder).appendChild(emptyEl);
        }
        emptyEl.textContent = mode === DELIVERY_MODE
          ? locale('location_inventory_empty_delivery', 'Nothing is available for shipping right now. Check back soon.')
          : locale('location_inventory_empty', 'Nothing is in stock at %store% right now. Try another store or check back soon.').replace('%store%', storeLabel);
        emptyEl.hidden = false;
        const productsBlock = holder.querySelector('.search-block');
        if (productsBlock && !productsBlock.querySelector('[data-js-search-item][data-variant-id]:not([data-fulfillment-hidden="true"])')) {
          productsBlock.style.display = '';
          const title = productsBlock.querySelector('.search-title');
          if (title) title.style.display = 'none';
        }
      } else if (emptyEl) {
        emptyEl.hidden = true;
        emptyEl.textContent = '';
      }
    });

    const countEl = document.getElementById('CollectionProductCount');
    if (countEl && storeHandle) {
      countEl.setAttribute('data-js-location-count', 'true');
      countEl.textContent = showingText;
    }

    document.querySelectorAll('.shopify-section').forEach((section) => {
      if (section.querySelector('#main-collection-product-grid, #CollectionProductGrid')) return;
      const items = section.querySelectorAll('[data-js-product-item][data-variant-id]');
      if (items.length < 2) return;
      const visible = Array.from(items).filter((item) => item.getAttribute(HIDDEN_ATTR) !== 'true');
      if (!visible.length) {
        section.style.display = 'none';
        section.setAttribute('data-js-location-section-hidden', 'true');
      } else if (section.hasAttribute('data-js-location-section-hidden')) {
        section.style.display = '';
        section.removeAttribute('data-js-location-section-hidden');
      }
    });
  }

  async function mapInBatches(items, worker, size = 6) {
    const results = [];
    for (let i = 0; i < items.length; i += size) {
      const batch = items.slice(i, i + size);
      results.push(...await Promise.all(batch.map(worker)));
    }
    return results;
  }

  async function filterSite() {
    const token = ++filterToken;
    const storeHandle = getInventoryStore();

    if (!storeHandle) {
      revealAll();
      return;
    }

    const cards = Array.from(document.querySelectorAll(CARD_SELECTOR));
    const pages = Array.from(document.querySelectorAll(PAGE_SELECTOR));

    cards.forEach((card) => {
      const variantId = variantIdFrom(card);
      const known = (card.hasAttribute('data-store-availability') && mapFromElement(card)) || cacheGet(variantId);
      if (isLocationExempt(card)) {
        showEl(card);
      } else if (known) {
        if (isStoreInMap(known, storeHandle)) showEl(card);
        else hideEl(card);
      } else {
        hideEl(card);
      }
    });
    updateCartRecommendations();

    await mapInBatches(cards, (card) => applyCard(card, storeHandle));
    if (token !== filterToken) return;

    await mapInBatches(pages, (page) => applyProductPage(page, storeHandle));
    if (token !== filterToken) return;

    updateEmptyStates(storeHandle);
  }

  function cartRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  async function getCart() {
    const response = await fetch(`${cartRoot()}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return { item_count: 0, attributes: {} };
    return response.json();
  }

  function sameFulfillment(mode, storeHandle) {
    const currentMode = localStorage.getItem(STORAGE_MODE) || 'pickup';
    const currentStore = localStorage.getItem(STORAGE_STORE);
    return currentMode === mode && currentStore === storeHandle;
  }

  async function stampCart(mode, storeHandle) {
    const label = mode === DELIVERY_MODE
      ? locale('store_selector_delivery_label', 'Shipping')
      : getStoreLabel(storeHandle);
    const body = new URLSearchParams({
      'attributes[pickup_location]': storeHandle || '',
      'attributes[pickup_location_name]': label || '',
      'attributes[fulfillment_mode]': mode || 'pickup'
    });
    await fetch(`${cartRoot()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
      body
    });
  }

  function emptyMoney() {
    const sample = document.querySelector('[data-header-cart-total]');
    const text = (sample && sample.textContent) || '$0.00';
    return text.replace(/[\d,.]+/, '0.00');
  }

  function refreshHeaderCart(count, totalText) {
    document.querySelectorAll('[data-header-cart-count]').forEach((el) => {
      el.textContent = String(count);
    });
    document.querySelectorAll('[data-header-cart-total]').forEach((el) => {
      el.textContent = totalText;
    });
    const drawer = document.getElementById('site-cart-sidebar');
    if (drawer) {
      drawer.setAttribute('data-cart-items', String(count));
      drawer.classList.toggle('cart-is-empty', Number(count) === 0);
    }
  }

  async function refreshCartDom() {
    try {
      const response = await fetch('?section_id=helper-cart');
      const text = await response.text();
      if (document.getElementById('AjaxCartForm') && typeof window.renderHelperCart === 'function') {
        window.renderHelperCart(text);
        return;
      }
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const cartCount = doc.querySelector('[data-cart-count]');
      const cartTotal = doc.querySelector('[data-cart-total]');
      refreshHeaderCart(
        cartCount ? cartCount.textContent : '0',
        cartTotal ? cartTotal.textContent : emptyMoney()
      );
    } catch (error) {
      refreshHeaderCart(0, emptyMoney());
    }
  }

  async function clearCart() {
    const response = await fetch(`${cartRoot()}cart/clear.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' }
    });
    await refreshCartDom();
    document.dispatchEvent(new CustomEvent('krown:cart:add', { bubbles: true }));
    return response.ok;
  }

  function localeReplace(template, storeLabel) {
    return (template || '').replace(/%store%/g, storeLabel || 'this store');
  }

  function showCartSwitchModal({ title, copy, onConfirm, onCancel }) {
    const modal = document.getElementById('modal-store-cart-switch');
    if (!modal || typeof modal.show !== 'function') {
      if (window.confirm(copy)) onConfirm();
      else if (onCancel) onCancel();
      return;
    }

    const titleEl = modal.querySelector('[data-js-cart-switch-title]');
    const copyEl = modal.querySelector('[data-js-cart-switch-copy]');
    const confirmBtn = modal.querySelector('[data-js-cart-switch-confirm]');
    const cancelBtn = modal.querySelector('[data-js-cart-switch-cancel]');
    if (titleEl) titleEl.textContent = title;
    if (copyEl) copyEl.textContent = copy;

    const finish = (fn) => {
      confirmBtn?.removeEventListener('click', onYes);
      cancelBtn?.removeEventListener('click', onNo);
      modal.querySelectorAll('[data-js-close]').forEach((el) => el.removeEventListener('click', onNo));
      if (fn) fn();
    };
    const onYes = async (event) => {
      event?.preventDefault();
      confirmBtn.disabled = true;
      try {
        await onConfirm();
      } finally {
        confirmBtn.disabled = false;
        modal.hide();
        finish();
      }
    };
    const onNo = (event) => {
      event?.preventDefault();
      modal.hide();
      finish(onCancel);
    };

    confirmBtn?.addEventListener('click', onYes);
    cancelBtn?.addEventListener('click', onNo);
    modal.querySelectorAll('[data-js-close]').forEach((el) => el.addEventListener('click', onNo));
    modal.show();
  }

  async function requestFulfillmentChange(mode, storeHandle, proceed) {
    if (!storeHandle) {
      proceed();
      return;
    }
    if (sameFulfillment(mode, storeHandle)) {
      proceed();
      return;
    }

    let itemCount = 0;
    try {
      const cart = await getCart();
      itemCount = cart.item_count || 0;
    } catch (error) {
      itemCount = 0;
    }

    const go = async () => {
      try {
        await stampCart(mode, storeHandle);
      } catch (error) {
        /* cart stamp is best-effort; store switch still proceeds */
      }
      proceed();
    };

    if (!itemCount) {
      await go();
      return;
    }

    const storeLabel = mode === DELIVERY_MODE
      ? locale('store_selector_delivery_label', 'Shipping')
      : getStoreLabel(storeHandle);
    showCartSwitchModal({
      title: locale('store_selector_cart_switch_title', 'Switch stores?'),
      copy: localeReplace(
        locale('store_selector_cart_switch_copy', 'Switching to %store% empties your cart. You can only check out from one store.'),
        storeLabel
      ),
      onConfirm: async () => {
        await clearCart();
        await go();
      }
    });
  }

  async function ensureCartStamp() {
    const storeHandle = getInventoryStore();
    const mode = getMode();
    if (!storeHandle) return;
    try {
      const cart = await getCart();
      if (!(cart.item_count || 0)) return;
      const attrs = cart.attributes || {};
      if (attrs.pickup_location === storeHandle && attrs.fulfillment_mode === mode) return;
      await stampCart(mode, storeHandle);
    } catch (error) {
      /* ignore */
    }
  }

  async function isAvailableAtStoreStrict(variantId, storeHandle, el) {
    if (isLocationExempt(el)) return true;
    if (!variantId || !storeHandle) return false;
    const embedded = el ? mapFromElement(el) : null;
    if (embedded) {
      cacheSet(variantId, embedded);
      return isStoreInMap(embedded, storeHandle);
    }
    const cached = cacheGet(variantId);
    if (cached) return isStoreInMap(cached, storeHandle);
    const map = await fetchAvailabilityMap(variantId);
    if (!map) return false;
    return isStoreInMap(map, storeHandle);
  }

  window.LetsDishLocationInventory = {
    getMode,
    getInventoryStore,
    getStoreLabel,
    isDeliveryCollectionPath,
    resolveInventoryHandles,
    isStoreInMap,
    isLocationExempt,
    getMap: fetchAvailabilityMap,
    isAvailableAtStore,
    isAvailableAtStoreStrict,
    filterSite,
    cacheSet,
    cacheGet,
    getCart,
    stampCart,
    clearCart,
    requestFulfillmentChange,
    ensureCartStamp
  };

  function updateCartStoreLabels() {
    const mode = getMode();
    const storeHandle = getInventoryStore();
    const pickupStore = getStore();
    let label = '';
    if (mode === DELIVERY_MODE) {
      label = locale('store_selector_delivery_label', 'Shipping');
    } else if (pickupStore) {
      label = getStoreLabel(pickupStore);
    }
    document.querySelectorAll('[data-js-cart-store-label]').forEach((el) => {
      // Drawer header in shipping mode reads "Cart (n)" (Austin, Oct 21). Cart page h1 unchanged.
      const text = mode === DELIVERY_MODE && el.closest('#site-cart-sidebar') ? 'Cart' : label;
      if (el.textContent !== text) el.textContent = text;
    });
    document.querySelectorAll('[data-js-cart-title]').forEach((el) => {
      el.hidden = mode === DELIVERY_MODE;
    });
    const itemNote = mode === DELIVERY_MODE
      ? locale('store_selector_delivery_label', 'Shipping')
      : (pickupStore ? `Pickup at ${getStoreLabel(pickupStore)}` : '');
    document.querySelectorAll('[data-js-cart-item-fulfillment]').forEach((el) => {
      if (el.textContent !== itemNote) el.textContent = itemNote;
      el.hidden = !itemNote;
    });
    updateCheckoutReminder(mode, pickupStore ? getStoreLabel(pickupStore) : '');
    updateShipProgress(mode);
  }

  // Shipping range bar in the cart drawer (snippets/cart-shipping-progress.liquid). Liquid renders the
  // amounts; this only shows it in shipping mode, since cart attributes can lag the picker.
  function updateShipProgress(mode) {
    const show = mode === DELIVERY_MODE;
    document.querySelectorAll('[data-js-cart-ship-progress]').forEach((el) => {
      if (el.hidden === !show) return;
      el.hidden = !show;
    });
  }

  function strong(text) {
    const el = document.createElement('strong');
    el.textContent = text;
    return el;
  }

  // Tells the shopper which checkout tab to pick. Checkout opens on Ship by default.
  function updateCheckoutReminder(mode, storeLabel) {
    const isShipping = mode === DELIVERY_MODE;
    const key = isShipping ? 'ship' : (storeLabel ? `pickup:${storeLabel}` : '');
    document.querySelectorAll('[data-js-cart-fulfillment-reminder]').forEach((box) => {
      box.hidden = !key;
      const textEl = box.querySelector('[data-js-cart-fulfillment-reminder-text]');
      if (!textEl || !key || textEl.dataset.key === key) return;
      textEl.dataset.key = key;
      textEl.replaceChildren(
        strong(isShipping ? 'Shipping.' : `Pickup at ${storeLabel}.`),
        ' At checkout, choose ',
        strong(isShipping ? 'Ship' : 'Pickup'),
        '.'
      );
    });
  }

  function watchCartDom() {
    let timer = null;
    const observer = new MutationObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(updateCartStoreLabels, 30);
    });
    document.querySelectorAll('[id="AjaxCartSubtotal"], [id="AjaxCartForm"]').forEach((el) => {
      observer.observe(el, { childList: true });
    });
    document.addEventListener('click', (event) => {
      if (!event.target.closest('[data-js-cart-fulfillment-change]')) return;
      event.preventDefault();
      const opened = document.querySelector('.sidebar--opened');
      if (opened && typeof opened.hide === 'function') opened.hide();
      document.getElementById('modal-store-selector')?.show();
    });
  }

  function bind() {
    filterSite();
    ensureCartStamp();
    updateCartStoreLabels();
    watchCartDom();

    document.addEventListener('storechanged', () => {
      filterSite();
      updateCartStoreLabels();
    });
    const refreshProductPurchase = (root) => {
      const page = root?.closest?.('[data-js-product-component], .main-product') || document.querySelector('.main-product[data-js-product-component], [data-js-product-component].main-product, .main-product');
      const storeHandle = getInventoryStore();
      if (page && storeHandle) applyProductPage(page, storeHandle);
    };
    document.addEventListener('change', (event) => {
      if (!event.target.closest('select, input, [data-js-product-variant-container]')) return;
      setTimeout(() => refreshProductPurchase(event.target), 40);
    });
    document.addEventListener('ld:pickup-availability', () => {
      refreshProductPurchase(document.querySelector('pickup-availability-compact'));
    });
    document.addEventListener('krown:main-collection-grid:filtered', filterSite);
    document.addEventListener('product-recommendations-loaded', filterSite);
    document.addEventListener('shopify:section:load', () => {
      filterSite();
      updateCartStoreLabels();
    });
    window.addEventListener('storage', (event) => {
      if (event.key === STORAGE_STORE || event.key === STORAGE_MODE) {
        filterSite();
        updateCartStoreLabels();
      }
    });

    let debounceTimer = null;
    const observer = new MutationObserver((mutations) => {
      const relevant = mutations.some((mutation) => {
        return Array.from(mutation.addedNodes).some((node) => {
          if (!(node instanceof Element)) return false;
          return node.matches?.(CARD_SELECTOR)
            || node.matches?.(PAGE_SELECTOR)
            || node.querySelector?.(CARD_SELECTOR)
            || node.querySelector?.(PAGE_SELECTOR);
        });
      });
      if (!relevant) return;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(filterSite, 120);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();
